import React, { useState, useEffect, useRef } from 'react';
import { submitDispute } from '../services/disputeService';
import { getStaff, Staff } from '../services/staffService';
import { getMasterTransactionsByEmail, getMasterTransactions } from '../services/allocationService';
import { MasterTransaction } from '../types';
import { isManagerialRole } from '../constants';
import { useFirebase } from '../contexts/FirebaseContext';
import { getLocalDate, parseTransactionDate } from '../lib/utils';
import { getSettings } from '../services/settingsService';
import { getIRSCashTipDeadline, formatLongDate } from '../lib/complianceUtils';
import { AlertCircle, Lock, FileText, Hash, Calendar, DollarSign, User, HelpCircle, CheckCircle2, ShieldAlert } from 'lucide-react';

interface Props {
  tenantId: string;
  prefillTip?: any;
  onSuccess?: () => void;
}

export const DisputeForm: React.FC<Props> = ({ tenantId, prefillTip, onSuccess }) => {
  const { user } = useFirebase();
  const [formData, setFormData] = useState({
    staffName: '',
    email: '',
    posId: '',
    tipDate: '',
    tipType: 'POS Tip' as 'Cash Tip' | 'POS Tip',
    originalAmount: 0,
    correctedAmount: 0,
    reason: 'Calculated Tip Discrepancy',
    hoursWorked: '',
    explanation: '',
    type: 'Allocation-Correction' as 'Submission-Correction' | 'Allocation-Correction',
    originalTipId: '',
    originalTipType: '' as 'Cash' | 'Jar' | 'POS' | '',
    masterTxId: '',
    docNumber: ''
  });

  const [staffList, setStaffList] = useState<Staff[]>([]);
  const [transactionList, setTransactionList] = useState<MasterTransaction[]>([]);
  const [searchTx, setSearchTx] = useState('');
  const [status, setStatus] = useState('');
  const [correctedAmountInput, setCorrectedAmountInput] = useState<string>('');
  const initializedRef = useRef<string | null>(null);

  // Initialize and prefill only once per prefilled transaction
  useEffect(() => {
    const init = async () => {
      const staff = await getStaff(tenantId);
      setStaffList(staff);

      if (!prefillTip) return;

      const prefillKey = prefillTip.id || prefillTip.masterTxId || `${prefillTip.tipDate || prefillTip.date}_${prefillTip.amount}`;
      if (initializedRef.current === prefillKey) return;
      initializedRef.current = prefillKey;

      const settings = await getSettings(tenantId);
      const timezone = settings?.timezone || 'UTC';
      const today = getLocalDate(timezone);

      const amount = prefillTip?.amount !== undefined 
        ? prefillTip.amount 
        : (prefillTip?.allocatedAmount !== undefined ? prefillTip.allocatedAmount : (prefillTip?.jarAmount || 0));

      const matchedStaff = staff.find(s => 
        (prefillTip.staffId && s.id === prefillTip.staffId) ||
        (prefillTip.email && s.email?.toLowerCase() === prefillTip.email.toLowerCase()) ||
        (prefillTip.staffName && s.name?.toLowerCase() === prefillTip.staffName.toLowerCase())
      );

      const docId = prefillTip.id || prefillTip.masterTxId || prefillTip.source_id || '';
      const docNum = prefillTip.source_id || prefillTip.docNumber || (docId ? (docId.startsWith('TX-') ? docId : `TX-${docId.slice(0, 8)}`) : '');

      setFormData(prev => ({
        ...prev,
        staffName: prefillTip.staffName || matchedStaff?.name || user?.displayName || '',
        email: prefillTip.email || matchedStaff?.email || user?.email || '',
        posId: prefillTip.posId || matchedStaff?.posId || '',
        tipDate: parseTransactionDate(prefillTip.tipDate || prefillTip.date || today),
        tipType: prefillTip.source === 'Cash' || prefillTip.source === 'Jar' ? 'Cash Tip' : 'POS Tip',
        originalAmount: Number(amount) || 0,
        correctedAmount: Number(amount) || 0,
        originalTipId: docId,
        originalTipType: prefillTip.source || prefillTip.type || 'POS',
        masterTxId: docId,
        docNumber: docNum,
        type: 'Allocation-Correction',
        reason: 'Calculated Tip Discrepancy'
      }));
      setCorrectedAmountInput((Number(amount) || 0).toFixed(2));
    };

    init();
  }, [prefillTip, tenantId, user]);

  // Load staff & historical transactions if needed for lookup
  useEffect(() => {
    const fetchData = async () => {
      const [staff] = await Promise.all([getStaff(tenantId)]);
      const currentUserStaff = staff.find(s => s.email.toLowerCase() === user?.email?.toLowerCase());
      
      let isAdminOrManager = false;
      try {
        isAdminOrManager = (currentUserStaff && (isManagerialRole(currentUserStaff.role) || ['Manager', 'Salon Manager', 'admin'].includes(currentUserStaff.role))) || user?.email?.toLowerCase().endsWith('@growezy.ai') || false;
      } catch (e) {
        isAdminOrManager = false;
      }

      if (!prefillTip) {
        const txs = isAdminOrManager ? await getMasterTransactions(tenantId) : await getMasterTransactionsByEmail(tenantId, user?.email || '');
        setTransactionList(txs);
      }
      
      const filteredStaff = isAdminOrManager ? staff : (currentUserStaff ? [currentUserStaff] : staff.filter(s => s.email.toLowerCase() === user?.email?.toLowerCase()));
      setStaffList(filteredStaff);
      
      if (currentUserStaff && !prefillTip && !formData.staffName) {
        const settings = await getSettings(tenantId);
        const timezone = settings?.timezone || 'UTC';
        const today = getLocalDate(timezone);
        setFormData(prev => ({ 
          ...prev, 
          tipDate: today, 
          staffName: currentUserStaff.name, 
          email: currentUserStaff.email,
          posId: currentUserStaff.posId || ''
        }));
      }
    };

    if (user?.email) {
      fetchData();
    }
  }, [tenantId, user?.email, prefillTip]);

  const isPrefilled = !!(prefillTip && (prefillTip.id || prefillTip.masterTxId || prefillTip.tipDate || prefillTip.date));

  const getComplianceInfo = () => {
    if (formData.tipType !== 'Cash Tip' || !formData.tipDate) return null;
    const parts = formData.tipDate.split('-');
    if (parts.length !== 3) return null;
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    
    const deadlineDate = getIRSCashTipDeadline(year, month);
    const today = new Date();
    const todayReset = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const isPastDeadline = todayReset > deadlineDate;
    const monthName = new Date(year, month, 1).toLocaleDateString('en-US', { month: 'long' });
    
    return {
      monthName,
      year,
      deadlineStr: formatLongDate(deadlineDate),
      isPastDeadline
    };
  };

  const compInfo = getComplianceInfo();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      let selectedStaff = staffList.find(s => s.name.toLowerCase() === formData.staffName.toLowerCase());
      if (!selectedStaff && user?.email) {
        selectedStaff = staffList.find(s => s.email.toLowerCase() === user.email?.toLowerCase());
      }

      if (!selectedStaff) {
        setStatus('Error: Please select a valid staff member.');
        return;
      }

      const targetAmount = parseFloat(correctedAmountInput) || 0;

      const parsedHours = formData.hoursWorked && !isNaN(parseFloat(formData.hoursWorked))
        ? parseFloat(formData.hoursWorked)
        : null;

      await submitDispute(tenantId, selectedStaff.posId, {
        tenantId,
        ...formData,
        description: formData.explanation,
        hoursWorked: parsedHours,
        staffId: selectedStaff.id,
        posId: selectedStaff.posId || null,
        originalAmount: Math.round(formData.originalAmount * 100) / 100,
        correctedAmount: Math.round(targetAmount * 100) / 100,
        status: 'Pending',
        correctionStatus: 'Pending'
      });

      setStatus('Dispute submitted successfully! It has been routed to manager review.');
      if (onSuccess) {
        setTimeout(() => {
          onSuccess();
        }, 1200);
      }
    } catch (error) {
      console.error('Error submitting dispute:', error);
      setStatus(`Error submitting dispute: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  const originalAmountNum = formData.originalAmount || 0;
  const correctedAmountNum = parseFloat(correctedAmountInput) || 0;
  const difference = correctedAmountNum - originalAmountNum;

  return (
    <form onSubmit={handleSubmit} className="p-6 md:p-8 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-gray-100 pb-5 gap-3">
        <div>
          <h2 className="text-xl font-black text-gray-900 tracking-tight flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-rose-500 shrink-0" />
            Report Tip Discrepancy & Adjustment Request
          </h2>
          <p className="text-xs text-gray-400 font-medium mt-1">
            Review recorded shift details, specify the correction reason, and enter your claimed amount.
          </p>
        </div>
        <span className="text-[10px] font-black uppercase tracking-widest bg-rose-50 text-rose-700 px-3 py-1.5 rounded-full border border-rose-100 flex items-center gap-1.5 self-start sm:self-center shrink-0">
          <ShieldAlert size={12} /> Audit-Logged
        </span>
      </div>

      {status && (
        <div className={`p-4 rounded-2xl text-xs font-bold ${
          status.includes('successfully') ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-rose-50 text-rose-800 border border-rose-200'
        }`}>
          {status}
        </div>
      )}

      {/* Manual Search (Only if opened without a pre-selected transaction) */}
      {!isPrefilled && (
        <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100">
          <label className="text-xs font-bold text-gray-900 uppercase tracking-widest block mb-1">
            Find Transaction to Dispute
          </label>
          <p className="text-[10px] text-gray-500 mb-2">
            Search by amount, date, or transaction ID to auto-populate the details.
          </p>
          <input
            type="text"
            placeholder="Search by ID, date, or amount..."
            value={searchTx}
            onChange={e => setSearchTx(e.target.value)}
            className="input-field w-full text-xs"
          />
          {searchTx && transactionList.length > 0 && (
            <div className="mt-2 bg-white border border-gray-200 rounded-xl max-h-40 overflow-y-auto shadow-md divide-y divide-gray-100">
              {transactionList
                .filter(tx => 
                  tx.id.toLowerCase().includes(searchTx.toLowerCase()) || 
                  (tx.amount?.toString().includes(searchTx)) ||
                  (tx.tipDate?.includes(searchTx))
                )
                .map(tx => (
                  <button
                    key={tx.id}
                    type="button"
                    className="w-full text-left p-3 hover:bg-gray-50 flex justify-between items-center text-xs"
                    onClick={() => {
                      const amount = tx.amount || 0;
                      setFormData(prev => ({
                        ...prev,
                        originalAmount: amount,
                        masterTxId: tx.id,
                        docNumber: tx.source_id || `TX-${tx.id.slice(0, 8)}`,
                        tipDate: parseTransactionDate(tx.tipDate),
                        staffName: tx.staffName || prev.staffName,
                        email: tx.email || prev.email
                      }));
                      setCorrectedAmountInput(amount.toFixed(2));
                      setSearchTx('');
                    }}
                  >
                    <div>
                      <span className="font-bold text-gray-900 block">{tx.staffName}</span>
                      <span className="text-[10px] text-gray-400 font-mono">ID: {tx.id.slice(0, 8)} • Date: {tx.tipDate}</span>
                    </div>
                    <span className="font-bold text-gray-900">${tx.amount?.toFixed(2)}</span>
                  </button>
                ))}
            </div>
          )}
        </div>
      )}

      {/* SECTION 1: Shift & Transaction Context (Locked / Read-Only Display) */}
      <div className="bg-slate-50 p-5 rounded-2xl border border-slate-200/80 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-xs font-black text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
            <FileText size={14} className="text-blue-600" />
            1. Shift & Recorded Information
          </h3>
          <span className="text-[10px] font-bold text-slate-500 flex items-center gap-1 bg-white px-2.5 py-1 rounded-lg border border-slate-200">
            <Lock size={11} className="text-slate-400" /> Recorded Ledger Data
          </span>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {/* Employee */}
          <div className="bg-white p-3.5 rounded-xl border border-slate-200/60 shadow-2xs">
            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block flex items-center gap-1">
              <User size={10} /> Employee
            </span>
            <span className="text-xs font-black text-slate-900 block truncate mt-0.5">
              {formData.staffName || 'Staff Member'}
            </span>
            {formData.posId && (
              <span className="text-[9px] font-bold text-blue-600 block mt-0.5">
                POS ID: {formData.posId}
              </span>
            )}
          </div>

          {/* Shift Date */}
          <div className="bg-white p-3.5 rounded-xl border border-slate-200/60 shadow-2xs">
            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block flex items-center gap-1">
              <Calendar size={10} /> Tip / Shift Date
            </span>
            <span className="text-xs font-black text-slate-900 block mt-0.5">
              {formData.tipDate || '—'}
            </span>
            <span className="text-[9px] font-medium text-slate-400 block mt-0.5">
              {formData.tipType}
            </span>
          </div>

          {/* Document / Transaction # (Displayed clearly when available) */}
          <div className="bg-white p-3.5 rounded-xl border border-slate-200/60 shadow-2xs">
            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block flex items-center gap-1">
              <Hash size={10} /> Document / Tx #
            </span>
            <span className="text-xs font-mono font-bold text-slate-800 block truncate mt-0.5" title={formData.docNumber || formData.masterTxId || 'Assigned on submission'}>
              {formData.docNumber || (formData.masterTxId ? (formData.masterTxId.startsWith('TX-') ? formData.masterTxId : `TX-${formData.masterTxId.slice(0, 8)}`) : 'Auto-Assigned')}
            </span>
          </div>

          {/* Currently Recorded Amount (STRICTLY LOCKED) */}
          <div className="bg-amber-50/90 p-3.5 rounded-xl border border-amber-200 shadow-2xs">
            <span className="text-[10px] font-black text-amber-800 uppercase tracking-widest block flex items-center gap-1">
              <Lock size={10} /> Recorded Amount
            </span>
            <span className="text-sm font-black text-amber-950 block mt-0.5 font-mono">
              ${originalAmountNum.toFixed(2)}
            </span>
            <span className="text-[9px] font-bold text-amber-700 block mt-0.5">
              Locked in ledger
            </span>
          </div>
        </div>
      </div>

      {/* SECTION 2: Discrepancy Reason Selection */}
      <div className="bg-white p-5 rounded-2xl border border-gray-200 space-y-4">
        <h3 className="text-xs font-black text-gray-900 uppercase tracking-wider flex items-center gap-1.5">
          <HelpCircle size={14} className="text-indigo-600" />
          2. Reason for Discrepancy
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-bold text-gray-700 block mb-1.5">
              Select Discrepancy Category <span className="text-rose-500">*</span>
            </label>
            <select
              value={formData.reason}
              onChange={e => setFormData(prev => ({ ...prev, reason: e.target.value }))}
              className="input-field w-full text-xs font-bold bg-gray-50 border border-gray-200 focus:bg-white rounded-xl py-2.5 cursor-pointer"
              required
            >
              <option value="Calculated Tip Discrepancy">Calculated Tip Discrepancy (Shift pool miscalculated)</option>
              <option value="Shift Hours Inaccurate">Shift Hours Inaccurate (Clock-in / timesheet hours wrong)</option>
              <option value="Missing from Schedule">Missing from Schedule / Shift Allocation</option>
              <option value="POS Tip Override">Credit Card (POS) Tip Not Allocated</option>
              <option value="Cash / Jar Share Missing">Cash or Jar Share Allocation Missing</option>
              <option value="Other Financial Correction">Other Financial Adjustment Request</option>
            </select>
            <p className="text-[11px] text-gray-400 mt-1.5 font-medium">
              Choose the category that best explains the issue for management audit.
            </p>
          </div>

          {/* Conditional Hours Worked field (helpful if disputing timesheets) */}
          <div>
            <label className="text-xs font-bold text-gray-700 block mb-1.5">
              Actual Hours Worked (Optional)
            </label>
            <input
              type="number"
              step="0.1"
              min="0"
              value={formData.hoursWorked}
              onChange={e => setFormData(prev => ({ ...prev, hoursWorked: e.target.value }))}
              className="input-field w-full text-xs bg-gray-50 border border-gray-200 focus:bg-white rounded-xl py-2.5"
              placeholder="e.g. 7.5 hrs"
            />
            <p className="text-[11px] text-gray-400 mt-1.5 font-medium">
              If disputing hours, enter the actual total hours worked on this shift.
            </p>
          </div>
        </div>
      </div>

      {/* SECTION 3: Target Amount (The only amount input required) */}
      <div className="bg-white p-5 rounded-2xl border border-gray-200 space-y-4">
        <h3 className="text-xs font-black text-gray-900 uppercase tracking-wider flex items-center gap-1.5">
          <DollarSign size={14} className="text-emerald-600" />
          3. Corrected Amount Requested
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
          <div>
            <label className="text-xs font-black text-blue-900 block mb-1.5 flex items-center gap-1">
              Target Amount Claimed ($) <span className="text-rose-500">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3.5 top-2.5 text-blue-600 text-sm font-bold">$</span>
              <input
                type="text"
                value={correctedAmountInput}
                onChange={e => {
                  const val = e.target.value;
                  if (val === '' || /^\d*\.?\d*$/.test(val)) {
                    setCorrectedAmountInput(val);
                  }
                }}
                className="input-field w-full pl-8 text-sm font-mono font-black bg-blue-50/40 text-blue-950 border border-blue-300 focus:border-blue-600 focus:bg-white rounded-xl py-2.5"
                placeholder="0.00"
                required
              />
            </div>
            <p className="text-[11px] text-blue-600 font-medium mt-1.5">
              Enter the accurate total tip amount you believe you should receive for this entry.
            </p>
          </div>

          {/* Dynamic Variance Box */}
          <div className={`p-4 rounded-xl border ${
            difference > 0 ? 'bg-emerald-50 border-emerald-200 text-emerald-800' :
            difference < 0 ? 'bg-rose-50 border-rose-200 text-rose-800' :
            'bg-gray-50 border-gray-200 text-gray-600'
          }`}>
            <span className="text-[10px] font-black uppercase tracking-wider block">
              Calculated Adjustment Impact
            </span>
            <div className="flex items-baseline gap-2 mt-1">
              <span className="text-lg font-black font-mono">
                {difference > 0 ? `+$${difference.toFixed(2)}` : difference < 0 ? `-$${Math.abs(difference).toFixed(2)}` : '$0.00'}
              </span>
              <span className="text-[11px] font-bold opacity-85">
                {difference > 0 ? '(Additional payout to you)' : difference < 0 ? '(Downward adjustment)' : '(No change)'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* SECTION 4: Explanation Note */}
      <div className="bg-white p-5 rounded-2xl border border-gray-200 space-y-3">
        <h3 className="text-xs font-black text-gray-900 uppercase tracking-wider flex items-center gap-1.5">
          <FileText size={14} className="text-gray-600" />
          4. Explanation for Manager Review
        </h3>
        <div>
          <label className="text-xs font-bold text-gray-700 block mb-1.5">
            Provide details or notes explaining the correction <span className="text-rose-500">*</span>
          </label>
          <textarea
            rows={3}
            value={formData.explanation}
            onChange={e => setFormData(prev => ({ ...prev, explanation: e.target.value }))}
            className="input-field w-full text-xs bg-gray-50 border border-gray-200 focus:bg-white rounded-xl p-3"
            placeholder="Please provide any relevant details (e.g., worked extra hours, split calculation missed a shift, etc.)."
            required
          />
        </div>
      </div>

      {/* IRS Compliance Info (Only for Cash Tips) */}
      {compInfo && (
        <div className={`p-4 rounded-xl border flex gap-3 text-xs ${
          compInfo.isPastDeadline ? 'bg-amber-50 border-amber-200 text-amber-900' : 'bg-emerald-50 border-emerald-200 text-emerald-900'
        }`}>
          <span className="text-base shrink-0">{compInfo.isPastDeadline ? '⚠️' : '📋'}</span>
          <div>
            <span className="font-bold uppercase tracking-wider text-[10px] block opacity-85">IRS Reporting Compliance</span>
            <span>Monthly cash tip deadline for {compInfo.monthName} {compInfo.year} was {compInfo.deadlineStr}.</span>
          </div>
        </div>
      )}

      {/* Submission Actions */}
      <div className="pt-2 flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="text-[11px] text-gray-500 flex items-center gap-1.5">
          <CheckCircle2 size={14} className="text-emerald-600 shrink-0" />
          <span>Your dispute will be logged with full audit tracking for manager approval.</span>
        </div>
        <button
          type="submit"
          disabled={status.includes('successfully')}
          className="btn-primary w-full sm:w-auto px-8 py-3.5 text-xs font-black uppercase tracking-widest !bg-rose-600 hover:!bg-rose-700 text-white rounded-xl shadow-md transition-all shrink-0"
        >
          Submit Dispute Request
        </button>
      </div>
    </form>
  );
};

